// Site access clearance — RON-5465. Spec: RON-4831 §6.7, acceptance criterion AC-P-67.
//
// A banner above the step cards that always states whether the contractor will be let on site.
//
// THE SERVER DECIDES THE STATE, we draw it. Until 22-October-2026 this derived the four states
// from `submitted` and the document rows, because `site_access` did not exist. Phase D sends it,
// and the Laravel team's instruction on the general case was blunt: *"We send it; please do not
// derive it."*
//
// Deleting the derivation rather than keeping it as a fallback is deliberate. A fallback is a
// second opinion nobody reads until the day it disagrees — and the disagreement here is a
// contractor told they are cleared when the server says they are not.
//
// WHY IT IS SEPARATE FROM THE "PACK SUBMITTED" CARD. §5.1 is explicit: the submitted banner
// "does not claim the contractor is cleared; clearance is shown by the site access banner".
// A pack can be submitted and the contractor still refused at the door, because submitting is
// something they do and approving is something a reviewer does.

import { useTranslation } from "react-i18next";

import {
  CLEARANCE,
  RENEWAL,
  formatLongDate,
  getClearanceTitleKey,
  getClearanceTone,
} from "../portalConstants";

const PortalClearanceBanner = ({ siteAccess }) => {
  const { t } = useTranslation("common");

  // No block, no claim. An absent banner is honest; one that says "not cleared yet" because a
  // field was missing is a sentence about the contractor that is not true.
  if (!siteAccess?.state) return null;

  const { state, needsAction, paused, clearedUntil, renewal, renewalUntil } = siteAccess;
  const tone = getClearanceTone(siteAccess);

  // The note under the title. Each is the sentence §6.7 attaches to that state, and each exists
  // because the title alone leaves a question the contractor would otherwise have to ask.
  const note = () => {
    if (state === CLEARANCE.BLOCKED) {
      // The COUNT goes on screen. "Two documents need action" is something a person can act on;
      // "not cleared" alone sends them through every card.
      return t(
        needsAction === 1
          ? "react_portal_clearance_blocked_note_one"
          : "react_portal_clearance_blocked_note"
      ).replace("%s", String(needsAction));
    }

    if (state === CLEARANCE.AWAITING) return t("react_portal_clearance_awaiting_note");

    if (state === CLEARANCE.CLEARED) {
      // AC-P-78. They are cleared TODAY and will not be after `renewal_until` unless they act —
      // which is a different sentence from "not cleared", and a more urgent one than it looks.
      if (renewal === RENEWAL.NEEDS_ACTION) {
        return t("react_portal_clearance_renewal_needed").replace(
          "%s",
          formatLongDate(renewalUntil)
        );
      }

      // AC-P-76. A renewal is with the reviewer and the current version is still valid, so
      // there is nothing for them to do — which is precisely what the note must say.
      if (renewal === RENEWAL.AWAITING) return t("react_portal_clearance_renewal_awaiting");

      return clearedUntil
        ? t("react_portal_clearance_cleared_note_dated").replace(
            "%s",
            formatLongDate(clearedUntil)
          )
        : t("react_portal_clearance_cleared_note");
    }

    // NOT_YET. When something already needs action the note says so, so the contractor sees the
    // problem before they press submit rather than after.
    return needsAction > 0
      ? t("react_portal_clearance_not_yet_note_action")
      : t("react_portal_clearance_not_yet_note");
  };

  return (
    <section
      className={`portal-clearance portal-clearance--${tone}`}
      // `status`, not `alert`. The banner is present on every render and changes as approvals
      // arrive; `alert` is assertive and would interrupt a screen reader mid-sentence each time.
      role="status"
      aria-live="polite"
      data-state={state}
    >
      <p className="portal-clearance__title">{t(getClearanceTitleKey(state))}</p>
      <p className="portal-clearance__note">{note()}</p>

      {/* A state we could not derive and did not know existed: a replaced document withdrew an
          approval that had already been granted. Said separately from the note, because it
          explains WHY the banner changed under somebody who had done nothing wrong. */}
      {paused && (
        <p className="portal-clearance__note">{t("react_portal_clearance_paused")}</p>
      )}
    </section>
  );
};

export default PortalClearanceBanner;
